import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import ClientInfoModal from "../client/ClientInfoModal";
import DateConverter from "../../utils/DateConverter";
import {
  PrimaryColor,
  TableHeaderGrey,
  TableBodyGrey,
  TableRowHover,
} from "../../shared/styles/colors";
import { AppointmentModal, Spinner } from "../index";
import { Card, CardContent, Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { styled } from "@mui/material/styles";
import DB_Status from "../../model/enum/status.class";
import Appointment from "../../model/appointment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CachedIcon from "@mui/icons-material/Cached";
import ApiService from "../../service/ApiService";
import Pathnames from "../../model/enum/Pathnames";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import PageResultResponse from "../../model/page-result-response";
import AppointmentDetailModal from "./AppointmentDetailModal";
import useStyles from "../styles/cardStyles";
import Client from "../../model/client";
import Misc from "../../utils/Misc";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
    "@media (max-width:600px)": {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:first-child th": {
    width: 20,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
  "@media (max-width:600px)": {
    height: 32, // decrease row height on mobile
    "&:first-child th": {
      width: 10, // decrease first column width on mobile
    },
  },
}));

const SORT_COLUMN = {
  CLIENT_ID: "clientId",
  SCHEDULED_AT: "scheduledAt",
  USER_ID: "userId",
  CREATED_AT: "createdAt",
  UPDATED_AT: "updatedAt",
  STATUS_ID: "statusId",
};

const SORT_DIRECTION = {
  ASC: "asc",
  DESC: "desc",
} as const;

const Appointments = () => {
  const classes = useStyles();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<string>("scheduledAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    SORT_DIRECTION.DESC,
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Update the URL with the current page and rowsPerPage
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  });

  //Get all appointments from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      searchParams.set("page", page.toString());
      searchParams.set("rowsPerPage", rowsPerPage.toString());
      searchParams.set("sortColumn", sortColumn);
      searchParams.set("sortDirection", sortDirection);

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.APPOINTMENTS}?${searchParams.toString()}`,
      ).then((data: PageResultResponse) => {
        if (Misc.isArrayEmpty(data.items)) return;
        const { items, count } = data;
        setAppointments(items);
        setTotalPages(count);
        // const today = new Date();
        // const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        // const upcomings = data.filter((appointment) => {
        //   const appointmentDate = new Date(appointment.scheduledAt).setHours(0, 0, 0, 0);
        //   const todayDate = today.setHours(0, 0, 0, 0);
        //   return appointmentDate >= todayDate && appointmentDate <= endOfMonth.setHours(0, 0, 0, 0);
        // });

        // const otherAppointments = data.filter(
        //   (appointment) =>
        //     new Date(appointment.scheduledAt).setHours(0, 0, 0, 0) > endOfMonth.setHours(0, 0, 0, 0)
        // );

        // upcomings.sort(
        //   (a, b) =>
        //     new Date(a.scheduledAt).getTime() -
        //     new Date(b.scheduledAt).getTime()
        // );

        // data = [...upcomings, ...otherAppointments];
      });
      setLoading(false);
    };

    if (loading && Misc.isArrayEmpty(appointments)) fetchData();
  }, [appointments]);

  const resetList = (): void => {
    setPage(1);
    setRowsPerPage(10);
    setAppointments([]);
    setLoading(true);
    if (searchParams.has("appointmentId")) {
      searchParams.delete("appointmentId");
      setSearchParams(searchParams);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number,
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setAppointments([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(
        sortDirection === SORT_DIRECTION.ASC
          ? SORT_DIRECTION.DESC
          : SORT_DIRECTION.ASC,
      );
    } else {
      setSortColumn(column);
      setSortDirection(SORT_DIRECTION.DESC);
    }
    scrollToTop();
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("appointmentId", id.toString());
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("appointmentId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <Card raised className={classes.card}>
        <div className={classes.headerContainer}>
          <Typography className={classes.title}>Appointments</Typography>
          <div className={classes.headerDiv}>
            <AppointmentModal resetList={resetList} />
            <Button onClick={resetList}>
              <CachedIcon />
            </Button>
          </div>
        </div>
        <CardContent>
          <Paper>
            <TableContainer>
              <Table
                stickyHeader
                aria-label="customized table"
                padding="normal"
                size="medium"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">
                      <MoreVertIcon />
                    </StyledTableCell>
                    <StyledTableCell
                      align="right"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort(SORT_COLUMN.CLIENT_ID)}
                    >
                      Client
                      {sortColumn === SORT_COLUMN.CLIENT_ID && (
                        <span>
                          {sortDirection === SORT_DIRECTION.ASC ? " ↑" : " ↓"}
                        </span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      align="right"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort(SORT_COLUMN.SCHEDULED_AT)}
                    >
                      Scheduled At
                      {sortColumn === SORT_COLUMN.SCHEDULED_AT && (
                        <span>
                          {sortDirection === SORT_DIRECTION.ASC ? " ↑" : " ↓"}
                        </span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell align="right">Notes</StyledTableCell>
                    <StyledTableCell
                      align="right"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort(SORT_COLUMN.USER_ID)}
                    >
                      Seller
                      {sortColumn === SORT_COLUMN.USER_ID && (
                        <span>
                          {sortDirection === SORT_DIRECTION.ASC ? " ↑" : " ↓"}
                        </span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      align="right"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort(SORT_COLUMN.STATUS_ID)}
                    >
                      Status
                      {sortColumn === SORT_COLUMN.STATUS_ID && (
                        <span>
                          {sortDirection === SORT_DIRECTION.ASC ? " ↑" : " ↓"}
                        </span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      align="right"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort(SORT_COLUMN.CREATED_AT)}
                    >
                      Created At
                      {sortColumn === SORT_COLUMN.CREATED_AT && (
                        <span>
                          {sortDirection === SORT_DIRECTION.ASC ? " ↑" : " ↓"}
                        </span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      align="right"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort(SORT_COLUMN.UPDATED_AT)}
                    >
                      Updated At
                      {sortColumn === SORT_COLUMN.UPDATED_AT && (
                        <span>
                          {sortDirection === SORT_DIRECTION.ASC ? " ↑" : " ↓"}
                        </span>
                      )}
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                {loading ? (
                  <Spinner />
                ) : (
                  <TableBody>
                    {appointments.length > 0 ? (
                      appointments.map((row: Appointment) => (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell align="left">
                            <AppointmentDetailModal
                              appointment={row}
                              resetList={resetList}
                              onOpen={() => {
                                addIdToQueryPath(row?.id);
                              }}
                              onClose={() => {
                                removeIdFromQueryPath();
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <ClientInfoModal
                              clientData={row.Client as Client}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <div className="d-flex row">
                              <span>
                                {DateConverter.toFullDate(row.scheduledAt)}
                              </span>
                              <span>
                                {DateConverter.toDayMonthExtend(
                                  row.scheduledAt,
                                )}
                              </span>
                            </div>
                            <span>
                              From:{" "}
                              {DateConverter.convertTo12HourFormat(
                                row.initialTime,
                              )}
                            </span>
                            <br />
                            <span>
                              To:{" "}
                              {DateConverter.convertTo12HourFormat(
                                row.finalTime,
                              )}
                            </span>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <span className={classes.limitedText}>
                              {row.notes}
                            </span>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.User?.firstName}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography
                              style={DB_Status.mapStatusColor(row.Status?.name)}
                            >
                              <div
                                style={DB_Status.mapStatusRoundedColor(
                                  row.Status?.name,
                                )}
                              ></div>
                              {row.Status?.name}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.updatedAt)}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableCell align="right">No items</StyledTableCell>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            component="div"
            count={totalPages}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      <ToastContainer />
    </>
  );
};

export default Appointments;
